import { useEffect } from 'react';
import { Labeled, Stack } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { FormField } from '~/components/atoms/FormField';
import { AppSelector } from '~/components/organisms/fields/AppSelector';
import { OrganizationSelector } from '~/components/organisms/fields/OrganizationSelector';
import { useNavigate } from '~/components/utils/Navigation';

export type ISidebarHeaderProps = IContainerProps & {
  organizationId?: string | null;
  appId?: string;
};

type INavigationFormValues = {
  organizationId?: string | null;
  appId?: string;
};

const styles = stylex.create({
  host: {
    borderBottomColor: colorSchemeTokens.outlineVariant,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    paddingTop: spacingTokens.padding$4,
    paddingBottom: spacingTokens.padding$7,
    paddingLeft: spacingTokens.padding$7,
    paddingRight: spacingTokens.padding$7,
  },
});

export const SidebarHeader: React.FC<ISidebarHeaderProps> = (props) => {
  const { sx, organizationId, appId } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const form = useForm<INavigationFormValues>({
    defaultValues: { organizationId, appId },
  });

  useEffect(() => {
    form.setValue('organizationId', organizationId);
  }, [form, organizationId]);

  const handleOrganizationChange = (newOrganizationId: string): void => {
    const nextUrl = newOrganizationId
      ? `/organizations/${newOrganizationId}`
      : '/';
    navigate(nextUrl);
  };

  const handleApplicationChange = (newAppId: string | null): void => {
    if (newAppId && appId !== newAppId) {
      navigate(`/apps/${newAppId}`);
    }
  };

  return (
    <Stack gap={4} sx={[styles.host, sx]}>
      <Labeled label={t('common.account')}>
        <FormField
          name="organizationId"
          control={form.control}
          as={OrganizationSelector}
          rules={{
            onChange: (event: React.ChangeEvent<HTMLSelectElement>) =>
              event.target.value !== '__create' &&
              handleOrganizationChange(event.target.value),
          }}
          variant="outlined"
          allowPersonalAccount
          canCreate
        />
      </Labeled>

      {form.watch('organizationId') !== null ? (
        <Labeled label={t('common.application')}>
          <FormField
            as={AppSelector}
            forwardError
            control={form.control}
            name="appId"
            organizationId={form.watch('organizationId') || undefined}
            rules={{
              onChange: (event: React.ChangeEvent<HTMLSelectElement>) =>
                handleApplicationChange(event.target.value),
            }}
            variant="outlined"
            placeholder={t('SidebarHeader.fields.applicationId.placeholder')}
          />
        </Labeled>
      ) : null}
    </Stack>
  );
};
